import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import PublicLayout from './PublicLayout';

// Custom hook for scroll animations
const useInView = () => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

// Individual feature section component
const FeatureDetail = ({ feature, index, isReversed }) => {
  const [ref, isInView] = useInView();

  return (
    <div 
      ref={ref}
      className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-12 items-start py-16 lg:py-24 transition-all duration-700 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      {/* Content Side */}
      <div className="flex-1 w-full">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-success-15 text-primary-500">
            {feature.icon}
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-success-10 border border-success-20 text-primary-500">
            {feature.badge}
          </span>
        </div>

        <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-text-primary">
          {feature.title}
        </h2>

        <p className="text-base lg:text-lg leading-relaxed mb-6 text-palegreen-75">
          {feature.description}
        </p>

        {/* Capabilities list */}
        <div className="space-y-3 mb-6">
          {feature.capabilities.map((cap, idx) => (
            <div 
              key={idx}
              className={`flex items-start gap-3 transition-all duration-500 ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'}`}
              style={{ transitionDelay: `${0.3 + idx * 0.1}s` }}
            >
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-success-20">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="#32CD32" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <span className="font-medium text-text-primary">{cap.title}</span>
                <span className="text-palegreen-60"> — {cap.desc}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Use case highlight */}
        {feature.useCase && (
          <div className="p-4 rounded-lg bg-success-5 border border-success-10">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#32CD32" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
              </svg>
              <span className="text-xs font-semibold uppercase tracking-wider text-primary-500">
                Use Case
              </span>
            </div>
            <p className="text-sm text-palegreen-70">
              {feature.useCase}
            </p>
          </div>
        )}
      </div>

      {/* Image Side */}
      {feature.image && (
        <div className="flex-1 w-full flex items-center">
          <div className="relative rounded-xl overflow-hidden w-full bg-gradient-forest border border-success-10 min-h-[400px] flex items-center justify-center p-5">
            <img
              src={feature.image}
              alt={feature.title}
              className="w-full h-auto object-contain block max-h-[600px] max-w-full"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {/* Subtle glow overlay */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-glow-radial" />
          </div>
        </div>
      )}
      {!feature.image && (
        <div className="flex-1 w-full">
          <div className="relative rounded-xl overflow-hidden p-8 bg-gradient-forest border border-success-10 min-h-[280px]">
            <div className="flex items-center justify-center h-full">
              <div className="w-24 h-24 rounded-2xl flex items-center justify-center bg-success-10 border border-success-20">
                {feature.icon}
              </div>
            </div>
            {/* Subtle glow overlay */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-glow-radial" />
          </div>
        </div>
      )}
    </div>
  );
};

FeatureDetail.propTypes = {
  feature: PropTypes.shape({
    icon: PropTypes.node,
    badge: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    capabilities: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      desc: PropTypes.string
    })),
    useCase: PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string
    }),
    image: PropTypes.string
  }).isRequired,
  index: PropTypes.number,
  isReversed: PropTypes.bool
};

const Features = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const features = [
    // 1. CREATE IN PLAIN ENGLISH
    {
      badge: 'Create',
      title: 'Build AI features in Plain English',
      description: 'Just describe what you want your AI to do. No coding, no tech skills needed — we\'ll create the perfect prompt for you in seconds.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
        </svg>
      ),
      image: '/prompt-input.png',
      capabilities: [
        { title: 'Plain English', desc: 'Describe what you need in your own words' },
        { title: 'Instant Creation', desc: 'Your AI prompt is ready in seconds' },
        { title: 'Step-by-Step Guide', desc: 'Simple wizard walks you through the process' },
        { title: 'Try It Immediately', desc: 'Test your prompt right after creating it' },
      ],
      useCase: 'Describe "I need AI that writes professional but friendly emails." In seconds, you have a working prompt ready to use.',
    },
    // 2. TEST INSTANTLY
    {
      badge: 'Test',
      title: 'Real-Time Prompt Execution',
      description: 'Run prompts against live APIs with full metrics. See token counts, latency, and cost per execution before deploying to production.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
        </svg>
      ),
      image: '/realtime-prompt-execution.png',
      capabilities: [
        { title: 'Live API Calls', desc: 'Test against real OpenAI, Anthropic, Groq APIs' },
        { title: 'Token Metrics', desc: 'Input/output token counts per run' },
        { title: 'Cost Tracking', desc: 'Real-time cost calculation' },
        { title: 'Run History', desc: 'Complete log of all executions' },
      ],
      useCase: 'Validate your prompt with edge cases before production. See exactly what each run costs and how the model responds.',
    },
    // 3. IMPROVE WITH AI
    {
      badge: 'Refine',
      title: 'Make It Better with AI Help',
      description: 'Want better results? Just say what to change — "make it shorter" or "add bullet points" — and AI improves your prompt automatically.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
        </svg>
      ),
      image: '/iterative-prompt-improvement.png',
      capabilities: [
        { title: 'Just Ask', desc: 'Describe improvements in plain English' },
        { title: 'See Changes', desc: 'Review what\'s different before accepting' },
        { title: 'Your Choice', desc: 'Accept, reject, or tweak any suggestion' },
        { title: 'Undo Anytime', desc: 'Every change can be reversed if needed' },
      ],
      useCase: 'Your AI responses are too long. Just type "make it more concise" and review the improvements before applying them.',
    },
    // 4. COMPARE AI MODELS
    {
      badge: 'Compare',
      title: 'Side-by-Side Model Comparison',
      description: 'Run the same prompt on two models simultaneously. Compare output quality, token usage, latency, and cost in real-time to find the best fit.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
      image: '/model-comparison.png',
      capabilities: [
        { title: 'Two Models at Once', desc: 'Compare any two models from OpenAI, Anthropic, or Groq' },
        { title: 'Live Streaming', desc: 'Watch both responses generate in real-time' },
        { title: 'Cost & Token Analysis', desc: 'Exact token counts and cost per model' },
        { title: 'Comparison History', desc: 'Save and review past comparisons' },
      ],
      useCase: 'Compare GPT-4o vs Claude Sonnet for your use case — see which gives better results at what cost, then try another pair.',
    },
    // 5. NEVER LOSE YOUR WORK
    {
      badge: 'Version Control',
      title: 'Git for Prompts',
      description: 'Every change is tracked with timestamps and commit messages. Work on drafts privately, preview diffs, and rollback instantly if needed.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
        </svg>
      ),
      image: '/version-control.png',
      capabilities: [
        { title: 'Draft Mode', desc: 'Work on changes privately before publishing' },
        { title: 'Visual Diff', desc: 'Line-by-line comparison between versions' },
        { title: 'Instant Rollback', desc: 'Restore any previous version immediately' },
        { title: 'AI Commit Messages', desc: 'Auto-generated summaries of your changes' },
        { title: 'Conflict Detection', desc: 'Warns if someone else edited the prompt' },
      ],
      useCase: 'Experiment with major changes in a draft, preview the diff, and only publish when ready. Broke something? Rollback in seconds.',
    },
    // 6. TRACK YOUR SPENDING
    {
      badge: 'Analytics',
      title: 'Know Exactly What You Spend',
      description: 'See your AI costs in real-time. No surprise bills. Understand where your money goes and stay in control of your budget.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
        </svg>
      ),
      image: '/monitoring.png',
      capabilities: [
        { title: 'Real-Time Costs', desc: 'See spending as it happens' },
        { title: 'Usage Insights', desc: 'Know which prompts you use most' },
        { title: 'Performance Stats', desc: 'Track speed and reliability over time' },
        { title: 'Full History', desc: 'Complete log of everything you\'ve run' },
      ],
      useCase: 'See that your email prompt is your most-used, costing $12/month — and find ways to optimize if needed.',
    },
    // 7. ALL YOUR AI IN ONE PLACE
    {
      badge: 'Library',
      title: 'All Your Prompts in One Place',
      description: 'Keep all your AI prompts organized and easy to find. Search, organize into projects, and access everything from one dashboard.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
        </svg>
      ),
      image: '/prompt-library.png',
      capabilities: [
        { title: 'Organize by Project', desc: 'Group related prompts together' },
        { title: 'Instant Search', desc: 'Find any prompt in seconds' },
        { title: 'Quick Access', desc: 'Your recent and favorite prompts at the top' },
        { title: 'Separate Workspaces', desc: 'Keep work and personal projects separate' },
      ],
      useCase: 'Managing 20+ prompts? Organize them by project, search instantly, and never lose track of your work.',
    },
    // 8. WORK AS A TEAM
    {
      badge: 'Teams',
      title: 'Work Together with Your Team',
      description: 'Share prompts with teammates. Control who can view, edit, or run them. Collaborate without stepping on each other\'s toes.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
        </svg>
      ),
      image: '/team-collaboration.png',
      capabilities: [
        { title: 'Shared Workspace', desc: 'One place for all team prompts' },
        { title: 'Control Access', desc: 'Decide who can edit vs. just view' },
        { title: 'Share Projects', desc: 'Give access to specific projects only' },
        { title: 'Shared API Keys', desc: 'Team uses the same AI account' },
      ],
      useCase: 'Your team of 5 shares prompts for customer support. Everyone can use them, but only admins can edit.',
    },
    // 9. FINE-TUNE YOUR RESULTS
    {
      badge: 'Configuration',
      title: 'Full Model Parameter Control',
      description: 'Adjust temperature, max tokens, top_p, frequency penalty, and presence penalty. Switch between models while preserving settings.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      image: '/fine-grained-parameter-control.png',
      capabilities: [
        { title: 'Temperature', desc: '0.0-2.0 for consistency vs creativity' },
        { title: 'Max Tokens', desc: 'Control output length and cost' },
        { title: 'Sampling Params', desc: 'top_p, frequency_penalty, presence_penalty' },
        { title: 'Model Switching', desc: 'Same settings across different models' },
      ],
      useCase: 'Set temperature=0.1 for factual Q&A, temperature=0.9 for creative writing. Fine-tune penalties to reduce repetition.',
    },
    // 10. EXPORT ANYWHERE
    {
      badge: 'Export',
      title: 'Export to Any Platform',
      description: 'Take your prompts anywhere. Export as ready-to-use code for OpenAI, Anthropic, LangChain, LiteLLM, or as cURL commands and Markdown docs.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>
      ),
      image: '/export.png',
      capabilities: [
        { title: 'OpenAI SDK', desc: 'Copy-paste Python code for OpenAI' },
        { title: 'Anthropic SDK', desc: 'Ready-to-run Claude integration' },
        { title: 'LangChain & LiteLLM', desc: 'Popular framework formats' },
        { title: 'cURL & Markdown', desc: 'API calls and documentation' },
      ],
      useCase: 'Built a prompt in ForgeVault but need it in your existing codebase? Export as OpenAI SDK code and paste directly into your project.',
    },
    // 11. USE ANYWHERE - SDK
    {
      badge: 'Developers',
      title: 'Production-Ready Python SDK',
      description: 'Integrate prompts into any Python application. Available on PyPI with full async support, caching, version pinning, and offline fallback.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
        </svg>
      ),
      image: '/forgevault-sdk.png',
      capabilities: [
        { title: 'PyPI Package', desc: 'pip install forgevault — Python 3.8+' },
        { title: 'Version Pinning', desc: 'Lock to specific prompt versions in production' },
        { title: 'Built-in Caching', desc: 'Local cache reduces latency and API calls' },
        { title: 'Async Support', desc: 'Full async/await for modern applications' },
        { title: 'Offline Fallback', desc: 'Cached prompts work when service is unreachable' },
      ],
      useCase: 'forge.get_prompt("customer-support").run(model="gpt-4o", query=user_input) — update prompts in ForgeVault, no redeploy needed.',
    },
    // 12. CLI FOR DEVELOPERS
    {
      badge: 'CLI',
      title: 'Command-Line Interface',
      description: 'Test and manage prompts from your terminal. List, run, and render prompts without leaving your development environment.',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
        </svg>
      ),
      image: '/cli.png',
      capabilities: [
        { title: 'forgevault run', desc: 'Execute prompts with variables from terminal' },
        { title: 'forgevault list', desc: 'Browse all your prompts' },
        { title: 'forgevault render', desc: 'Preview formatted prompts before running' },
        { title: 'Streaming Output', desc: 'Watch responses stream in real-time' },
      ],
      useCase: 'Testing a prompt change? Run `forgevault run "My Prompt" --model gpt-4o --var "input=test"` right from your terminal.',
    },
  ];

  return (
    <PublicLayout>
      <div className="px-6 lg:px-12 pt-8 pb-12 lg:pt-12 lg:pb-16">
        <div className="max-w-6xl mx-auto">
          
          {/* Hero Section */}
          <div 
            className={`text-center mb-16 lg:mb-24 transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '0.1s' }}
          >
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img 
                src="/logo.png"
                alt="ForgeVault Logo" 
                className="w-20 h-20 lg:w-24 lg:h-24 object-contain"
              />
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold mb-6 leading-tight text-text-primary">
              Everything You Need
              <span className="block mt-2 bg-clip-text text-transparent bg-gradient-text-green">
                to Build AI features
              </span>
            </h1>
            <p className="text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed text-palegreen-70">
              Create, test, improve, and deploy AI prompts that work exactly how you want.
              No coding to start, production-ready SDK when you need it.
            </p>
          </div>

          {/* Quick Overview Grid */}
          <div 
            className={`grid grid-cols-2 md:grid-cols-5 gap-3 mb-16 lg:mb-24 transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.2s' }}
          >
            {[
              { label: 'Create', desc: 'In plain English' },
              { label: 'Test', desc: 'See it working' },
              { label: 'Compare', desc: 'Two models at once' },
              { label: 'Improve', desc: 'With AI help' },
              { label: 'Export', desc: 'Use anywhere' },
            ].map((item, idx) => (
              <div 
                key={idx}
                className="text-center p-4 rounded-lg bg-bg-dark-accent border border-success-10"
              >
                <div className="text-lg font-bold mb-1 text-primary-500">
                  {item.label}
                </div>
                <div className="text-xs text-palegreen-60">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="h-px mb-8 bg-gradient-divider" />

          {/* Feature Details - Alternating layout */}
          {features.map((feature, index) => (
            <React.Fragment key={index}>
              <FeatureDetail 
                feature={feature} 
                index={index} 
                isReversed={index % 2 === 1}
              />
              {index < features.length - 1 && (
                <div className="h-px bg-gradient-divider-subtle" />
              )}
            </React.Fragment>
          ))}

          {/* CTA Section */}
          <div 
            className={`mt-16 lg:mt-24 text-center transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.5s' }}
          >
            <div className="p-8 lg:p-12 rounded-2xl bg-gradient-forest-subtle border border-success-15">
              <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-text-primary">
                Ready to build your first AI feature?
              </h2>
              <p className="text-base mb-8 max-w-xl mx-auto text-palegreen-60">
                Start free, no credit card needed. Create your first AI feature in under 5 minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/signup"
                  className="px-8 py-4 text-base font-semibold rounded-xl transition-all duration-300 inline-flex items-center justify-center gap-2 bg-gradient-cta text-black shadow-glow hover:-translate-y-0.5 hover:shadow-glow-lg"
                >
                  Start Building Free
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-4 text-base font-medium rounded-xl transition-all duration-300 inline-flex items-center justify-center bg-transparent border border-success-20 text-palegreen-80 hover:bg-success-10 hover:border-success-50"
                >
                  Learn About Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default Features;
